import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { organizationSettingsInput } from '@barbeiropro/contracts';
import { Field } from '@/components/shared/field';
import { MaskedInput } from '@/components/shared/masked-input';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { maskCnpj, maskPhone } from '@/lib/format';
import type { OrgSettings } from '@/lib/org';
import { ColorField } from '../catalog/color-field';
import { ImageUploadField } from '../catalog/image-upload';
import { applyServerError, emptyToNull, zodForm } from '../catalog/lib';
import { Section } from './section';

export const TIMEZONES: [string, string][] = [
  ['America/Sao_Paulo', 'Brasília (Sudeste, Sul, Nordeste, Goiás)'],
  ['America/Manaus', 'Manaus (Amazonas)'],
  ['America/Cuiaba', 'Cuiabá (Mato Grosso)'],
  ['America/Campo_Grande', 'Campo Grande (Mato Grosso do Sul)'],
  ['America/Porto_Velho', 'Porto Velho (Rondônia)'],
  ['America/Boa_Vista', 'Boa Vista (Roraima)'],
  ['America/Rio_Branco', 'Rio Branco (Acre)'],
  ['America/Belem', 'Belém (Pará, Amapá)'],
  ['America/Fortaleza', 'Fortaleza (Ceará, Rio Grande do Norte, Piauí)'],
  ['America/Recife', 'Recife (Pernambuco, Paraíba)'],
  ['America/Maceio', 'Maceió (Alagoas, Sergipe)'],
  ['America/Bahia', 'Salvador (Bahia)'],
  ['America/Araguaina', 'Araguaína (Tocantins)'],
  ['America/Noronha', 'Fernando de Noronha'],
];

interface Values {
  name: string;
  slug: string;
  cnpj: string;
  phone: string;
  whatsapp: string;
  email: string;
  timezone: string;
  instagram: string;
  facebook: string;
  tiktok: string;
  brandPrimary: string;
  brandAccent: string;
  logoAssetId: string | null;
  faviconAssetId: string | null;
}

const toPayload = (v: Values) => ({
  name: v.name,
  slug: v.slug,
  cnpj: emptyToNull(v.cnpj),
  phone: emptyToNull(v.phone),
  whatsapp: emptyToNull(v.whatsapp),
  email: emptyToNull(v.email),
  timezone: v.timezone,
  instagram: emptyToNull(v.instagram),
  facebook: emptyToNull(v.facebook),
  tiktok: emptyToNull(v.tiktok),
  brandPrimary: v.brandPrimary,
  brandAccent: v.brandAccent,
  logoAssetId: v.logoAssetId,
  faviconAssetId: v.faviconAssetId,
});

const fromOrg = (o: OrgSettings): Values => ({
  name: o.name,
  slug: o.slug,
  cnpj: o.cnpj ? maskCnpj(o.cnpj) : '',
  phone: o.phone ? maskPhone(o.phone) : '',
  whatsapp: o.whatsapp ? maskPhone(o.whatsapp) : '',
  email: o.email ?? '',
  timezone: o.timezone,
  instagram: o.instagram ?? '',
  facebook: o.facebook ?? '',
  tiktok: o.tiktok ?? '',
  brandPrimary: o.brandPrimary,
  brandAccent: o.brandAccent,
  logoAssetId: o.logoAssetId,
  faviconAssetId: o.faviconAssetId,
});

export function BusinessPage() {
  const qc = useQueryClient();
  const org = useQuery({ queryKey: ['org', 'settings'], queryFn: () => api.get<OrgSettings>('/org/settings') });
  const form = useForm<Values>({ resolver: zodForm<Values>(organizationSettingsInput, toPayload) });
  const { errors, isDirty } = form.formState;

  useEffect(() => {
    if (org.data) form.reset(fromOrg(org.data));
  }, [org.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (v: Values) => api.put<OrgSettings>('/org/settings', organizationSettingsInput.parse(toPayload(v))),
    onSuccess: (o) => {
      toast.success('Dados da barbearia salvos');
      qc.setQueryData(['org', 'settings'], o);
      qc.invalidateQueries({ queryKey: ['org'] });
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      qc.invalidateQueries({ queryKey: ['site'] });
    },
    onError: (e) => applyServerError(form, e),
  });

  if (org.isLoading) return <LoadingState />;
  if (org.error) return <ErrorState error={org.error} onRetry={() => org.refetch()} />;

  const slug = form.watch('slug');
  const publicUrl = `${window.location.origin}/b/${slug || '…'}`;

  return (
    <form onSubmit={form.handleSubmit((v) => save.mutate(v))} className="grid max-w-3xl gap-8" noValidate>
      <Section title="Dados da barbearia" description="Aparecem no site, nos e-mails e nos recibos.">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="org-name" label="Nome" error={errors.name?.message} className="sm:col-span-2">
            <Input id="org-name" aria-invalid={!!errors.name} {...form.register('name')} />
          </Field>
          <Field id="org-slug" label="Endereço público" error={errors.slug?.message} className="sm:col-span-2" hint="Só letras minúsculas, números e hífens.">
            <Input id="org-slug" aria-invalid={!!errors.slug} autoCapitalize="none" spellCheck={false} {...form.register('slug')} />
            <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              Seus clientes acessam em
              <a href={publicUrl} target="_blank" rel="noreferrer" className="tabular inline-flex items-center gap-1 font-medium text-foreground underline underline-offset-4">
                {publicUrl} <ExternalLink className="size-3" aria-hidden />
              </a>
            </p>
          </Field>
          <Field id="org-cnpj" label="CNPJ (opcional)" error={errors.cnpj?.message}>
            <MaskedInput mask="cnpj" id="org-cnpj" placeholder="00.000.000/0000-00" aria-invalid={!!errors.cnpj} {...form.register('cnpj')} />
          </Field>
          <Field id="org-email" label="E-mail de contato (opcional)" error={errors.email?.message}>
            <Input id="org-email" type="email" aria-invalid={!!errors.email} {...form.register('email')} />
          </Field>
          <Field id="org-phone" label="Telefone (opcional)" error={errors.phone?.message}>
            <MaskedInput mask="phone" id="org-phone" placeholder="(00) 0000-0000" aria-invalid={!!errors.phone} {...form.register('phone')} />
          </Field>
          <Field id="org-whatsapp" label="WhatsApp (opcional)" error={errors.whatsapp?.message}>
            <MaskedInput mask="phone" id="org-whatsapp" placeholder="(00) 00000-0000" aria-invalid={!!errors.whatsapp} {...form.register('whatsapp')} />
          </Field>
          <Field id="org-tz" label="Fuso horário" error={errors.timezone?.message} className="sm:col-span-2" hint="Define o horário da agenda, dos lembretes e dos relatórios.">
            <Controller
              control={form.control}
              name="timezone"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="org-tz" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map(([v, l]) => (
                      <SelectItem key={v} value={v}>
                        {l}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
        </div>
      </Section>

      <Section title="Redes sociais" description="Links completos, com https://.">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field id="org-ig" label="Instagram" error={errors.instagram?.message}>
            <Input id="org-ig" type="url" placeholder="https://instagram.com/…" aria-invalid={!!errors.instagram} {...form.register('instagram')} />
          </Field>
          <Field id="org-fb" label="Facebook" error={errors.facebook?.message}>
            <Input id="org-fb" type="url" placeholder="https://facebook.com/…" aria-invalid={!!errors.facebook} {...form.register('facebook')} />
          </Field>
          <Field id="org-tt" label="TikTok" error={errors.tiktok?.message}>
            <Input id="org-tt" type="url" placeholder="https://tiktok.com/@…" aria-invalid={!!errors.tiktok} {...form.register('tiktok')} />
          </Field>
        </div>
      </Section>

      <Section title="Identidade visual" description="Cores e imagens usadas no site público e nos e-mails enviados aos clientes.">
        <div className="grid gap-5 sm:grid-cols-2">
          <Controller control={form.control} name="brandPrimary" render={({ field }) => <ColorField id="org-primary" label="Cor principal" value={field.value ?? ''} onChange={field.onChange} error={errors.brandPrimary?.message && 'Use o formato #RRGGBB'} />} />
          <Controller control={form.control} name="brandAccent" render={({ field }) => <ColorField id="org-accent" label="Cor de destaque" value={field.value ?? ''} onChange={field.onChange} error={errors.brandAccent?.message && 'Use o formato #RRGGBB'} />} />
          <Controller control={form.control} name="logoAssetId" render={({ field }) => <ImageUploadField value={field.value} onChange={field.onChange} purpose="logo" label="Logo" defaultAlt={`Logo ${form.getValues('name')}`} shape="wide" />} />
          <Controller control={form.control} name="faviconAssetId" render={({ field }) => <ImageUploadField value={field.value} onChange={field.onChange} purpose="logo" label="Favicon" defaultAlt={`Ícone ${form.getValues('name')}`} hint="Imagem quadrada, aparece na aba do navegador." />} />
        </div>
      </Section>

      <div>
        <Button type="submit" disabled={save.isPending || !isDirty}>
          {save.isPending ? 'Salvando…' : 'Salvar dados da barbearia'}
        </Button>
      </div>
    </form>
  );
}
